import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';

const Recipient = ({
  recipient,
  index,
  updateData,
  data,
  sms,
  theme,
  formatPhoneNumber,
  CARRIER_MAP,
}) => {
  const [localRecipient, setLocalRecipient] = useState(recipient.recipient || '');
  const [localCarrier, setLocalCarrier] = useState(() => {
    if (!recipient.carrier) return '';
    const byName = CARRIER_MAP.find((c) => c.name === recipient.carrier);
    if (byName) return byName.name;
    const bySms = CARRIER_MAP.find((c) => c.sms === recipient.carrier);
    return bySms ? bySms.name : '';
  });

  useEffect(() => {
    if (!recipient.carrier) return setLocalCarrier('');
    const byName = CARRIER_MAP.find((c) => c.name === recipient.carrier);
    if (byName) return setLocalCarrier(byName.name);
    const bySms = CARRIER_MAP.find((c) => c.sms === recipient.carrier);
    setLocalCarrier(bySms ? bySms.name : '');
  }, [recipient.carrier, CARRIER_MAP]);

  const carriers = useMemo(
    () => [...CARRIER_MAP].sort((a, b) => a.name.localeCompare(b.name)),
    [CARRIER_MAP],
  );

  const stripCarrierDomain = (value) => {
    if (!value) return value;
    for (const carrier of CARRIER_MAP) {
      if (value.endsWith(carrier.sms)) return value.slice(0, -carrier.sms.length);
    }
    return value;
  };

  const commitRecipient = (nextRecipient, nextCarrierName = localCarrier) => {
    let base = nextRecipient ?? localRecipient;
    if (sms) base = stripCarrierDomain(base);

    let carrierSms = '';
    if (sms && nextCarrierName) {
      const found = CARRIER_MAP.find((c) => c.name === nextCarrierName);
      carrierSms = found ? found.sms : '';
    }
    const finalValue = sms && carrierSms ? `${base}${carrierSms}` : base;

    const newRecipients = [...data.recipients];
    newRecipients[index] = {
      ...recipient,
      recipient: finalValue,
      carrier: nextCarrierName || '',
    };

    updateData({
      recipients: newRecipients,
      recipient: newRecipients.map((r) => r.recipient).join(','),
    });
  };

  const handleRecipientChange = (e) => {
    let value = e.target.value;
    if (sms) {
      value = value.replace(/[^\d]/g, '');
      if (!/^\d{0,10}$/.test(value)) return;
    }
    setLocalRecipient(value);
  };

  const handleRecipientBlur = () => commitRecipient(localRecipient);

  const handleCarrierChange = (e) => {
    const name = e.target.value;
    setLocalCarrier(name);
    commitRecipient(localRecipient, name);
  };

  const removeEnabled = index !== 0 || (index === 0 && data.recipients.length > 1);
  const isLast = index === data.recipients.length - 1;

  return (
    <div className="w-100 mb-3">
      <div
        className={`row ${index === 0 ? 'g-1 align-items-end ' : 'g-1 align-items-center pt-4  border-top '} `}
      >
        {/* Recipient field */}
        <div className="d-flex flex-row gap-2 w-100 mb-1">
          <div className="flex-grow-1">
            <label className="form-label">Recipient</label>
            <input
              className="form-control bg-base rounded-1 border-0"
              type={sms ? 'tel' : 'text'}
              value={sms ? formatPhoneNumber(localRecipient) : localRecipient}
              onChange={handleRecipientChange}
              onBlur={handleRecipientBlur}
              onKeyDown={(e) => e.key === 'Enter' && handleRecipientBlur()}
              inputMode={sms ? 'numeric' : undefined}
            />
          </div>
          {/* Remove button */}
          {removeEnabled && (
            <div className="d-flex align-items-end" style={{ minWidth: 40 }}>
              <Button
                variant="primary"
                className="rounded-2 w-100"
                aria-label="Remove recipient"
                onClick={() => {
                  const newRecipients = [...data.recipients];
                  newRecipients.splice(index, 1);
                  updateData({
                    recipients: newRecipients,
                    recipient: newRecipients.map((r) => r.recipient).join(','),
                  });
                }}
              >
                &minus;
              </Button>
            </div>
          )}
        </div>
        {/* Carrier select (SMS only) */}
        {sms && (
          <div className="mt-2" style={{ minWidth: 220 }}>
            <label className="form-label">Carrier</label>
            <select
              className="form-select bg-base rounded-1"
              value={localCarrier}
              onChange={handleCarrierChange}
            >
              <option value="">Select Carrier</option>
              {carriers.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Add button */}
        {isLast && (
          <div className="col-12 col-md-auto mt-3">
            <Button
              variant="primary"
              className="rounded-2 w-100"
              aria-label="Add recipient"
              onClick={() => {
                updateData({
                  recipients: [...data.recipients, { recipient: '', carrier: '' }],
                });
              }}
            >
              + Add Recipient
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recipient;
